import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon, HeartIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/utils/cn';
import { RatingStars } from '@/components/ui/RatingStars';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { formatCurrency } from '@/utils/formatters';

export interface Product {
  id: number;
  name: string;
  price: number;
  originalPrice?: number;
  image: string;
  rating?: number;
  reviewCount?: number;
  category?: string;
  isNew?: boolean;
  isBestSeller?: boolean;
  isRecycled?: boolean;
  discount?: number;
  inStock?: boolean;
  createdAt?: string;
}

interface ProductCarouselProps {
  title?: string;
  subtitle?: string;
  products: Product[];
  itemsPerView?: {
    mobile: number;
    tablet: number;
    desktop: number;
  };
  autoPlay?: boolean;
  autoPlayInterval?: number;
  showNavigation?: boolean;
  showDots?: boolean;
  gap?: number;
  onProductClick?: (product: Product) => void;
  className?: string;
}

export function ProductCarousel({
  title,
  subtitle,
  products,
  itemsPerView = { mobile: 1, tablet: 2, desktop: 4 },
  autoPlay = false,
  autoPlayInterval = 5000,
  showNavigation = true,
  showDots = false,
  gap = 16,
  onProductClick,
  className,
}: ProductCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsToShow, setItemsToShow] = useState(itemsPerView.desktop);
  const [wishlist, setWishlist] = useState<Set<number>>(new Set());
  const carouselRef = useRef<HTMLDivElement>(null);
  const autoPlayRef = useRef<NodeJS.Timeout>();

  // Responsive items per view
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setItemsToShow(itemsPerView.mobile);
      } else if (window.innerWidth < 1024) {
        setItemsToShow(itemsPerView.tablet);
      } else {
        setItemsToShow(itemsPerView.desktop);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [itemsPerView]);

  // Auto-play functionality
  useEffect(() => {
    if (autoPlay && products.length > itemsToShow) {
      autoPlayRef.current = setInterval(() => {
        handleNext();
      }, autoPlayInterval);

      return () => {
        if (autoPlayRef.current) {
          clearInterval(autoPlayRef.current);
        }
      };
    }
  }, [autoPlay, autoPlayInterval, currentIndex, itemsToShow, products.length]);

  const maxIndex = Math.max(0, products.length - itemsToShow);

  const handlePrev = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(maxIndex, prev + 1));
  };

  const toggleWishlist = (productId: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setWishlist((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex < maxIndex;

  return (
    <div className={cn('relative', className)}>
      {/* Header */}
      {(title || subtitle) && (
        <div className="flex items-end justify-between mb-6">
          <div>
            {title && (
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-1">
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="text-gray-600 dark:text-gray-400">{subtitle}</p>
            )}
          </div>

          {/* Navigation Arrows (Desktop) */}
          {showNavigation && products.length > itemsToShow && (
            <div className="hidden md:flex items-center gap-2">
              <button
                onClick={handlePrev}
                disabled={!canGoPrev}
                className={cn(
                  'w-10 h-10 rounded-full flex items-center justify-center',
                  'border-2 border-gray-300 dark:border-dark-600',
                  'transition-all duration-200',
                  canGoPrev
                    ? 'text-gray-700 dark:text-gray-300 hover:bg-primary-500 hover:border-primary-500 hover:text-white'
                    : 'text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-50'
                )}
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
              <button
                onClick={handleNext}
                disabled={!canGoNext}
                className={cn(
                  'w-10 h-10 rounded-full flex items-center justify-center',
                  'border-2 border-gray-300 dark:border-dark-600',
                  'transition-all duration-200',
                  canGoNext
                    ? 'text-gray-700 dark:text-gray-300 hover:bg-primary-500 hover:border-primary-500 hover:text-white'
                    : 'text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-50'
                )}
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Carousel */}
      <div className="relative overflow-hidden" ref={carouselRef}>
        <motion.div
          className="flex"
          animate={{
            x: `-${currentIndex * (100 / itemsToShow)}%`,
          }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          style={{ gap: `${gap}px` }}
        >
          {products.map((product) => (
            <motion.div
              key={product.id}
              className="flex-shrink-0"
              style={{ width: `calc(${100 / itemsToShow}% - ${(gap * (itemsToShow - 1)) / itemsToShow}px)` }}
              whileHover={{ y: -4 }}
              transition={{ duration: 0.2 }}
            >
              <Link
                to={`/products/${product.id}`}
                onClick={() => onProductClick?.(product)}
                className="block group"
              >
                <div
                  className={cn(
                    'bg-white dark:bg-dark-800',
                    'border border-gray-200 dark:border-dark-700',
                    'rounded-xl overflow-hidden',
                    'transition-all duration-200',
                    'hover:shadow-lg hover:border-primary-300 dark:hover:border-primary-700'
                  )}
                >
                  {/* Image Container */}
                  <div className="relative aspect-square overflow-hidden bg-gray-100 dark:bg-dark-700">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    />

                    {/* Badges */}
                    <div className="absolute top-2 left-2 flex flex-col gap-1.5">
                      {product.isNew && (
                        <Badge variant="success" className="shadow-md">
                          New
                        </Badge>
                      )}
                      {product.isBestSeller && (
                        <Badge variant="warning" className="shadow-md">
                          Best Seller
                        </Badge>
                      )}
                      {product.isRecycled && (
                        <Badge variant="info" className="shadow-md">
                          Recycled
                        </Badge>
                      )}
                      {product.discount && (
                        <Badge variant="error" className="shadow-md">
                          -{product.discount}%
                        </Badge>
                      )}
                    </div>

                    {/* Wishlist Button */}
                    <button
                      onClick={(e) => toggleWishlist(product.id, e)}
                      className={cn(
                        'absolute top-2 right-2',
                        'w-9 h-9 rounded-full',
                        'bg-white/90 dark:bg-dark-900/90 backdrop-blur-sm',
                        'flex items-center justify-center',
                        'transition-all duration-200',
                        'hover:scale-110 shadow-md'
                      )}
                    >
                      {wishlist.has(product.id) ? (
                        <HeartSolidIcon className="h-5 w-5 text-red-500" />
                      ) : (
                        <HeartIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                      )}
                    </button>

                    {/* Quick View Button (appears on hover) */}
                    <div className="absolute inset-x-0 bottom-0 translate-y-full group-hover:translate-y-0 transition-transform duration-200">
                      <Button
                        size="sm"
                        variant="secondary"
                        className="w-full rounded-none"
                        onClick={(e) => {
                          e.preventDefault();
                          // TODO: Implement quick view modal
                        }}
                      >
                        Quick View
                      </Button>
                    </div>
                  </div>

                  {/* Product Info */}
                  <div className="p-4">
                    {/* Category */}
                    {product.category && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wide">
                        {product.category}
                      </p>
                    )}

                    {/* Product Name */}
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2 line-clamp-2 min-h-[2.5rem]">
                      {product.name}
                    </h3>

                    {/* Rating */}
                    {product.rating !== undefined && (
                      <div className="flex items-center gap-2 mb-2">
                        <RatingStars rating={product.rating} size="sm" />
                        {product.reviewCount !== undefined && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            ({product.reviewCount})
                          </span>
                        )}
                      </div>
                    )}

                    {/* Price */}
                    <div className="flex items-baseline gap-2">
                      <span className="text-lg font-bold text-primary-600 dark:text-primary-400">
                        {formatCurrency(product.price)}
                      </span>
                      {product.originalPrice && (
                        <span className="text-sm text-gray-500 dark:text-gray-400 line-through">
                          {formatCurrency(product.originalPrice)}
                        </span>
                      )}
                    </div>

                    {/* Stock Status */}
                    {product.inStock === false && (
                      <p className="text-xs text-error-500 mt-2">Out of Stock</p>
                    )}
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Dots Navigation */}
      {showDots && products.length > itemsToShow && (
        <div className="flex justify-center gap-2 mt-6">
          {Array.from({ length: maxIndex + 1 }).map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={cn(
                'w-2 h-2 rounded-full transition-all duration-200',
                currentIndex === index
                  ? 'bg-primary-500 w-8'
                  : 'bg-gray-300 dark:bg-dark-600 hover:bg-gray-400 dark:hover:bg-dark-500'
              )}
            />
          ))}
        </div>
      )}

      {/* Mobile Navigation */}
      {showNavigation && products.length > itemsToShow && (
        <div className="md:hidden flex justify-center gap-4 mt-6">
          <button
            onClick={handlePrev}
            disabled={!canGoPrev}
            className={cn(
              'w-12 h-12 rounded-full flex items-center justify-center',
              'border-2 border-gray-300 dark:border-dark-600',
              'transition-all duration-200',
              canGoPrev
                ? 'text-gray-700 dark:text-gray-300 active:bg-primary-500 active:border-primary-500 active:text-white'
                : 'text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-50'
            )}
          >
            <ChevronLeftIcon className="h-6 w-6" />
          </button>
          <button
            onClick={handleNext}
            disabled={!canGoNext}
            className={cn(
              'w-12 h-12 rounded-full flex items-center justify-center',
              'border-2 border-gray-300 dark:border-dark-600',
              'transition-all duration-200',
              canGoNext
                ? 'text-gray-700 dark:text-gray-300 active:bg-primary-500 active:border-primary-500 active:text-white'
                : 'text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-50'
            )}
          >
            <ChevronRightIcon className="h-6 w-6" />
          </button>
        </div>
      )}
    </div>
  );
}

// Skeleton Loader for ProductCarousel
export function ProductCarouselSkeleton({
  itemsToShow = 4,
  title,
}: {
  itemsToShow?: number;
  title?: string;
}) {
  return (
    <div className="relative">
      {title && (
        <div className="flex items-end justify-between mb-6">
          <div className="h-8 w-48 bg-gray-200 dark:bg-dark-700 rounded animate-pulse" />
        </div>
      )}
      <div className="flex gap-4">
        {Array.from({ length: itemsToShow }).map((_, i) => (
          <div key={i} className="flex-1">
            <div className="bg-gray-100 dark:bg-dark-800 rounded-xl overflow-hidden">
              <div className="aspect-square bg-gray-200 dark:bg-dark-700 animate-pulse" />
              <div className="p-4 space-y-3">
                <div className="h-4 bg-gray-200 dark:bg-dark-700 rounded animate-pulse" />
                <div className="h-4 bg-gray-200 dark:bg-dark-700 rounded w-3/4 animate-pulse" />
                <div className="h-6 bg-gray-200 dark:bg-dark-700 rounded w-1/2 animate-pulse" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
